/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useState, useEffect } from 'react';
import { Dialogs, Checkbox, Flex, NumericInput, Pane, Text } from '@leverege/ui-elements';
import { Attributes, DataSources } from '@leverege/ui-attributes';
import { GlobalState } from '@leverege/ui-redux';
import DeviceInput from '../../pairing/DeviceInput';
import InputGroup from './InputGroup';
import SalesFlowLocatorSearch from './SalesFlowLocatorSearch';
import { canSellVehicleWithUnpairedLocator } from '../../../util/RecovrSales';
import { UNPAIRED } from '../../../constants';
import Util from '../Util';
import WarrantyLengthEditor from '../../pairing/WarrantyLengthEditor';
import S from '../../pairing/PairingFlow.css';
import LocatorAtSisterLocation from './LocatorAtSisterLocation';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export default function RecovrInformation(props) {
  const {
    onChange,
    warrantyLength,
    locatorSalesPrice,
    locator,
    vehicle,
    location,
    actions,
    locatorBlueprint
  } = props;
  const serialNumber = Attributes.get('locator.serialNumber', 'locator', locator);
  const extendedWarrantyEnabled = Attributes.get('location.extendedWarrantyEnabled', 'location', location?.data);
  const [locatorId, setLocatorId] = useState(serialNumber);
  const [unpairedCheckbox, setUnpairedCheckbox] = useState(false);
  const [canSellWithUnpairedLocator, setCanSellWithUnpairedLocator] = useState(false);
  useEffect(() => {
    const fetchPermissionsForUnpairedLocator = async () => {
      const canSell = await canSellVehicleWithUnpairedLocator({
        actions
      });
      setCanSellWithUnpairedLocator(canSell);
    };
    fetchPermissionsForUnpairedLocator();
  }, [canSellWithUnpairedLocator]);
  const onNoLocatorId = () => {
    setUnpairedCheckbox(!unpairedCheckbox);
    if (!unpairedCheckbox) {
      setLocatorId(UNPAIRED);
      onChange({
        data: 'newLocator',
        value: {
          id: UNPAIRED
        }
      });
    } else {
      setLocatorId(serialNumber);
      onChange({
        data: 'newLocator',
        value: {}
      });
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onCancel = (confirm, value, object) => {
    // set value of locator if user confirms using sister location's locator
    if (confirm) {
      setLocatorId(value);
      if (object) {
        onChange({
          data: 'newLocator',
          value: object
        });
      }
    } else {
      setLocatorId(null);
    }
  };
  const onLocatorIdChange = useCallback(async ({
    data,
    value,
    object,
    evt
  }) => {
    // check locator for its location, if its at a different location, show warning dialog
    const locatorLocationId = object?.data?.location?.id;
    const locationId = actions.match.params.locationId;
    if (locationId !== locatorLocationId) {
      const loc = {
        id: locatorLocationId,
        ref: location.ref.substring(0, location.ref.lastIndexOf('/')),
        type: 'location'
      };
      const locatorLocationActions = DataSources.getActions(loc);
      const locatorLocation = await GlobalState.dispatch(locatorLocationActions.get());
      Dialogs.show({
        id: 'locatorAtSisterLocationDialog',
        component: LocatorAtSisterLocation,
        props: {
          ...props,
          locatorLocation,
          onCancel: confirm => onCancel(confirm, value, object)
        }
      });
    } else {
      setLocatorId(value);
      if (object) {
        onChange({
          data: 'newLocator',
          value: object
        });
      }
    }
  }, [onChange]);
  let warningMessage = '';
  if (locatorId && locatorId !== UNPAIRED) {
    const locatorUnactivated = locator && !Util.isLocatorActivated(locator);
    const locatorOffline = locator && Util.isLocatorOffline(locator);
    const locatorLowBattery = locator && Util.isLocatorLowBattery(locator);
    if (locatorUnactivated) {
      // eslint-disable-next-line max-len
      warningMessage = 'This Locator is unactivated and is not ready for sale. Please activate this Locator before continuing with the sale.';
    } else if (locatorLowBattery) {
      // eslint-disable-next-line max-len
      warningMessage = 'Locator battery is below the threshold recommended for sale. Please replace this Locator before continuing with the sale.';
    } else if (locatorOffline) {
      // eslint-disable-next-line max-len
      warningMessage = 'This Locator is offline and is not recommended for sale. Please replace this Locator before continuing with the sale.';
    }
  }
  const openSearch = useCallback(() => {
    Dialogs.show({
      id: 'salesFlowLocatorSearch',
      component: SalesFlowLocatorSearch,
      props: {
        actions,
        locatorBlueprint,
        location,
        eventData: 'locatorId',
        value: locatorId,
        onChange: onLocatorIdChange
      }
    });
  }, [locatorId, onLocatorIdChange, actions, locatorBlueprint, location]);
  return /*#__PURE__*/_jsxs(Pane, {
    layout: "flex:colL",
    children: [/*#__PURE__*/_jsx(Text, {
      variant: "bodyBold",
      children: "RecovR Locator"
    }), /*#__PURE__*/_jsxs(Flex, {
      variant: "rowL",
      align: "center",
      children: [/*#__PURE__*/_jsx("img", {
        width: 80,
        height: 80,
        alt: "RecovR Locator",
        src: "https://storage.googleapis.com/recovr-ui-assets/recovr-locator.png"
      }), /*#__PURE__*/_jsx(Text, {
        variant: "bodySmall",
        children: "The RecovR Locator offers vehicle location tracking, theft recovery assistance, and movement alerts."
      })]
    }), /*#__PURE__*/_jsx(InputGroup, {
      eventData: "locatorSalesPrice",
      onChange: onChange,
      editor: NumericInput,
      editorProps: {
        min: 0,
        float: true
      },
      value: locatorSalesPrice,
      title: "RecovR Sales Price"
    }), /*#__PURE__*/_jsx(InputGroup, {
      eventData: "trackerId",
      editor: DeviceInput,
      disabled: unpairedCheckbox,
      editorProps: {
        onClick: openSearch,
        onFocus: openSearch
      },
      value: locatorId,
      title: "Locator ID"
    }), canSellWithUnpairedLocator && /*#__PURE__*/_jsx(Checkbox, {
      eventData: "unpairedCheckbox",
      onChange: onNoLocatorId,
      value: unpairedCheckbox === true,
      disabled: !!vehicle.data.data.locator,
      label: "No paired locator"
    }), extendedWarrantyEnabled && /*#__PURE__*/_jsx(InputGroup, {
      eventData: "warrantyLength",
      editor: WarrantyLengthEditor,
      onChange: onChange,
      value: warrantyLength,
      title: "Warranty Length"
    }), warningMessage.length > 0 && /*#__PURE__*/_jsx(Flex, {
      variant: "colL",
      children: /*#__PURE__*/_jsxs(Pane, {
        className: S.container,
        layout: "flex:row",
        align: "start",
        children: [/*#__PURE__*/_jsx(Text, {
          className: S.icon,
          icon: "https://storage.googleapis.com/recovr-ui-assets/system-error.png"
        }), /*#__PURE__*/_jsxs(Text, {
          children: [/*#__PURE__*/_jsx("b", {
            children: "Warning:"
          }), " ", warningMessage]
        })]
      })
    })]
  });
}